import { GetStaticPaths, GetStaticProps } from "next";
import parlors from "goi_common/src/constants/parlors_detail.json";
import crematoriums from "goi_common/src/constants/hwajang_detail.json";
import facilities from "goi_common/src/constants/facilities_detail.json";
import { parlorRegion } from "@/constants/parlorRegion";
import {
  BTypeBreadCrumb,
  FUNERAL_SIDO,
  FullWidthButton,
  GOI_WEB_URL,
  GoiBinsoParagraph,
  MultipleMarkerMap,
  call,
  facilityDetailTypeName,
  facilityPublicTypeDescription,
  parlorManagedTypeDescription,
  parseFuneralTypeToDetailPrice,
} from "goi_common";
import { Box, Stack } from "@mui/material";
import Head from "next/head";

const { regionColor, targetSidos, baseUrl } = parlorRegion;

export const getStaticPaths: GetStaticPaths = async () => {
  const parlorList = parlors.filter((res) => targetSidos.includes(res.sido));
  const cremaList = crematoriums.filter((res) =>
    targetSidos.includes(String(res.sido))
  );
  const facilityList = facilities.filter((res) =>
    targetSidos.includes(String(res.sido))
  );

  let pathList = [] as Array<any>;

  parlorList.forEach((parlor) => {
    cremaList.forEach((crema) => {
      if (parlor.sigungu == String(crema.sigungu)) {
        facilityList.forEach((facility) => {
          if (parlor.sigungu == facility.sigungu) {
            pathList.push({
              params: {
                parlor: parlor.companyname,
                crema: crema.companyname,
                facility: facility.companyname,
              },
            });
          }
        });
      }
    });
  });

  return {
    paths: pathList,
    fallback: false,
  };
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
  let parlorDetail;
  let cremaDetail;
  let facilityDetail;
  if (params) {
    parlorDetail = parlors.filter((p) => p.companyname === params.parlor)[0];
    cremaDetail = crematoriums.filter((p) => p.companyname === params.crema)[0];
    facilityDetail = facilities.filter(
      (p) => p.companyname === params.facility
    )[0];
  }

  let description = `${parlorDetail?.companyname}, ${cremaDetail?.companyname}, ${facilityDetail?.companyname}, 대전충청 장례 한 번에 준비하자. 장례에 필요한 장례식장 준비부터 화장장, 장지 준비까지. 대전충청 지역 장례의 모든 것, 장례 114 : 대전충청장례정보포털.`;

  return {
    props: {
      parlorDetail,
      cremaDetail,
      facilityDetail,
      pageOgTitle: `${parlorDetail?.companyname}x${cremaDetail?.companyname}x${facilityDetail?.companyname}, 장례 준비는, 장례 114 ::  대전충청장례정보포털`,
      pageDescription: description,
      pageOgDescription: description,
      pageKeywords: `${parlorDetail?.companyname}, ${cremaDetail?.companyname}, ${facilityDetail?.companyname}, 대전충청 장례 준비, 대전 장례, 충청 장례, 장례 준비, 대전충청 장례식장, 대전 장례식장, 충청 장례식장, 대전충청 화장장, 대전충청 장지`,
      pageOgImage: parlorDetail?.images[0]?.url || "",
    },
  };
};

export default function allFuneral({
  parlorDetail,
  cremaDetail,
  facilityDetail,
  pageOgTitle,
}: any) {
  const regionName = "대전 · 충청";

  const facilityDescription = facilityPublicTypeDescription(
    facilityDetail.is_public
  );
  const facilityDetailType = facilityDetailTypeName(
    facilityDetail.type,
    facilityDetail.companyname
  );

  const carouselItems = [
    {
      "@type": "ListItem",
      name: `${parlorDetail.companyname}`,
      image: `${parlorDetail.images[0].url}`,
      url: `${baseUrl}/all-process-recommend/${parlorDetail.companyname}/${cremaDetail.companyname}/${facilityDetail.companyname}/`,
      position: 1,
    },
    {
      "@type": "ListItem",
      name: `${cremaDetail.companyname}`,
      image: `${cremaDetail.images[0].url}`,
      url: `${baseUrl}/all-process-recommend/${parlorDetail.companyname}/${cremaDetail.companyname}/${facilityDetail.companyname}/`,
      position: 2,
    },
    {
      "@type": "ListItem",
      name: `${facilityDetail.companyname}`,
      image: `${facilityDetail.images[0].url}`,
      url: `${baseUrl}/all-process-recommend/${parlorDetail.companyname}/${cremaDetail.companyname}/${facilityDetail.companyname}/`,
      position: 3,
    },
  ];

  const carouselRichSearch = {
    "@context": "http://schema.org",
    "@type": "ItemList",
    itemListElement: [
      ...carouselItems,
      {
        "@type": "ListItem",
        name: "장례준비가 처음이라면?",
        image:
          "https://goifuneral.mo.cloudinary.net/platform/prepare/prepare-1-1.png",
        url: `https://www.goifuneral.co.kr/funeral-prepare/1/`,
        position: "4",
      },
      {
        "@type": "ListItem",
        name: "장례식장, 음식비용 줄이기",
        image:
          "https://goifuneral.mo.cloudinary.net/platform/home/match-request-version/twemoji_balance-scale.png",
        url: `https://www.goifuneral.co.kr/funeral-prepare/funeral-cost/`,
        position: "5",
      },
      {
        "@type": "ListItem",
        name: "장례준비, 가장 먼저 알아볼 것은?",
        image:
          "https://goifuneral.mo.cloudinary.net/platform/prepare/prepare-1-1.png",
        url: `https://www.goifuneral.co.kr/funeral-prepare/1/`,
        position: "6",
      },
      {
        "@type": "ListItem",
        name: "장례식장 비용 비교",
        image:
          "https://goifuneral.mo.cloudinary.net/platform/home/match-request-version/twemoji_balance-scale.png",
        url: `https://www.goifuneral.co.kr/funeral-prepare/funeral-cost/`,
        position: "7",
      },
    ],
  };

  return (
    <>
      <Head>
        <title>{pageOgTitle}</title>

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(carouselRichSearch),
          }}
        />
      </Head>
      <BTypeBreadCrumb
        firstItem={{
          text: `장례 준비 한 번에`,
          href: GOI_WEB_URL + "/match/request/",
          event: {
            domain: "ch",
            path: "all_prcss_recm",
            trigger_and_target: "br_crumb_mth_req",
          },
        }}
        goiHomeEvent={{
          domain: "ch",
          path: "all_prcss_recm",
          trigger_and_target: "br_crumb_goi_home",
        }}
      />
      <Stack padding="40px 20px" style={{ backgroundColor: regionColor.c100 }}>
        <span className="subtitle_18_b" style={{ color: regionColor.c300 }}>
          {regionName}
        </span>
        <h1 className="subtitle_21_b"> 장례114 : 장례 준비 한 번에</h1>
      </Stack>

      <Stack
        padding="30px 20px 40px"
        margin="40px 0"
        gap="20px"
        style={{ backgroundColor: regionColor.c100 }}
      >
        <Box className="subtitle_18_b" color={regionColor.c300}>
          {regionName} 장례식장, 화장장, 장지 추천
        </Box>
        <Stack flexDirection="row" justifyContent="space-between">
          <Box className="body_14_m" color={regionColor.c300}>
            장례식장
          </Box>
          <Box className="body_14_m" style={{ width: "50%" }}>
            {parlorDetail.companyname}
          </Box>
        </Stack>
        <Stack flexDirection="row" justifyContent="space-between">
          <Box className="body_14_m" color={regionColor.c300}>
            화장장
          </Box>
          <Box className="body_14_m" style={{ width: "50%" }}>
            {cremaDetail.companyname}
          </Box>
        </Stack>
        <Stack flexDirection="row" justifyContent="space-between">
          <Box className="body_14_m" color={regionColor.c300}>
            장지
          </Box>
          <Box className="body_14_m" style={{ width: "50%" }}>
            {facilityDetail.companyname}
          </Box>
        </Stack>
        <FullWidthButton
          backgroundColor={regionColor.c400}
          text={`전체 비용 알아보기`}
          href={GOI_WEB_URL + "/match/request/"}
          event={{
            domain: "ch",
            path: "all_prcss_recm",
            trigger_and_target: "uv_mth_req",
          }}
        />
        <FullWidthButton
          backgroundColor={regionColor.c400}
          text={`상담 받기`}
          onClick={() => {
            call("1666-9784");
          }}
          event={{
            domain: "ch",
            path: "all_prcss_recm",
            trigger_and_target: "goi_call",
          }}
        />
      </Stack>

      <AllFuneralContent
        regionColor={regionColor}
        companyname={parlorDetail.companyname}
        address={FUNERAL_SIDO.get(Number(facilityDetail.sido))}
        description={`${parlorDetail.companyname}은 ${FUNERAL_SIDO.get(
          Number(parlorDetail.sido)
        )}에 위치한 ${parlorDetail.managed_type} 장례식장
      입니다. ${
        parlorManagedTypeDescription[
          parlorDetail.managed_type as "전문" | "공설" | "대학병원"
        ]
      }이기 때문에 추천드립니다.`}
        ctaDescription="조문객 수에 따라 예상 비용이 달라지기 때문에 반드시 상담을 통해 정확한 금액을 안내받으시길
        바랍니다."
        price={{
          least: parlorDetail.expected_charge_sum.무빈소,
          most: parlorDetail.expected_charge_sum.일반장,
        }}
        type="장례식장"
        detail={parlorDetail}
      />

      <AllFuneralContent
        regionColor={regionColor}
        companyname={cremaDetail.companyname}
        address={FUNERAL_SIDO.get(Number(facilityDetail.sido))}
        description={`${cremaDetail.companyname}은 ${FUNERAL_SIDO.get(
          cremaDetail.sido
        )}에 위치한 화장장입니다. 관내 주민인 경우, 더 저렴한 금액에 이용 가능합니다.`}
        ctaDescription="주소지에 따라 금액이 달라질 수 있으니 반드시 상담을 통해 정확한 금액을 안내받으시길 바랍니다."
        price={{
          least: cremaDetail.inside_price,
          most: cremaDetail.outside_price,
        }}
        type="화장장"
        detail={cremaDetail}
      />
      <AllFuneralContent
        regionColor={regionColor}
        companyname={facilityDetail.companyname}
        address={FUNERAL_SIDO.get(Number(facilityDetail.sido))}
        facilityType={facilityDetail.type}
        isPublicFacility={facilityDetail.is_public}
        description={`${facilityDetail.companyname}은 ${FUNERAL_SIDO.get(
          Number(facilityDetail.sido)
        )}에 위치한 ${
          facilityDetail.is_public ? "공설" : "사설"
        } ${facilityDetailType} 입니다. ${facilityDescription}`}
        ctaDescription="봉안당, 잔디장, 수목장 등 장지 형태, 국가 유공자 여부 등에 따라 가격이 달라지니, 반드시 상담을 통해 정확한 금액을 안내받으시길 바랍니다."
        type="장지"
        detail={facilityDetail}
      />

      <Stack
        padding="20px 20px"
        margin="10px 0"
        style={{ backgroundColor: regionColor.c100 }}
      >
        <Box className="subtitle_16_b">위치</Box>
        <MultipleMarkerMap
          parlor={parlorDetail}
          other={[facilityDetail, cremaDetail]}
        />
      </Stack>

      <Stack
        padding="30px 20px 40px"
        margin="40px 0 80px"
        gap="20px"
        style={{ backgroundColor: regionColor.c100 }}
      >
        <Box className="subtitle_18_b" color={regionColor.c300}>
          {regionName} 장례식장, 화장장, 장지 추천
        </Box>
        <Stack flexDirection="row" justifyContent="space-between">
          <Box className="body_14_m" color={regionColor.c300}>
            장례식장
          </Box>
          <Box className="body_14_m" style={{ width: "50%" }}>
            {parlorDetail.companyname}
          </Box>
        </Stack>
        <Stack flexDirection="row" justifyContent="space-between">
          <Box className="body_14_m" color={regionColor.c300}>
            화장장
          </Box>
          <Box className="body_14_m" style={{ width: "50%" }}>
            {cremaDetail.companyname}
          </Box>
        </Stack>
        <Stack flexDirection="row" justifyContent="space-between">
          <Box className="body_14_m" color={regionColor.c300}>
            장지
          </Box>
          <Box className="body_14_m" style={{ width: "50%" }}>
            {facilityDetail.companyname}
          </Box>
        </Stack>
        <FullWidthButton
          backgroundColor={regionColor.c400}
          text={`전체 비용 알아보기`}
          href={GOI_WEB_URL + "/match/request/"}
          event={{
            domain: "ch",
            path: "all_prcss_recm",
            trigger_and_target: "uv_mth_req_btom",
          }}
        />
        <FullWidthButton
          backgroundColor={regionColor.c400}
          text={`상담 받기`}
          onClick={() => {
            call("1666-9784");
          }}
          event={{
            domain: "ch",
            path: "all_prcss_recm",
            trigger_and_target: "goi_call_btom",
          }}
        />
      </Stack>
    </>
  );
}

function AllFuneralContent({
  description,
  price,
  type,
  facilityType,
  isPublicFacility,
  ctaDescription,
  companyname,
  regionColor,
  address,
}: any) {
  const priceResult = parseFuneralTypeToDetailPrice(
    type,
    price,
    facilityType,
    isPublicFacility,
    companyname
  );
  return (
    <Stack
      padding="20px 20px 40px"
      margin="20px 0px"
      gap="24px"
      style={{ backgroundColor: regionColor.c100 }}
    >
      <Box className="subtitle_21_b" component="h2">
        {companyname}
      </Box>

      <Box className="body_14_m gray900" component="p">
        {description}
      </Box>

      <Stack gap="10px">
        <Box className="subtitle_16_b">가격</Box>
        {priceResult && (
          <Box className="button_16_sb orange650" component="p">
            예상비용: {priceResult}
          </Box>
        )}
        <Box className="body_14_m gray900" component="p">
          {ctaDescription}

          <Box marginTop="12px" />
          <GoiBinsoParagraph
            descriptions={[
              "※ e하늘 장사정보시스템(보건복지부) 기반으로 산출된 값이며, (주)고이장례연구소는 시설과 무관합니다.",
            ]}
          />
        </Box>
      </Stack>
    </Stack>
  );
}
